import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UploadSimple } from "@phosphor-icons/react";
import { Button } from "./ui/button";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const variance = (actual, est) => {
  if (actual == null || est == null) return null;
  const d = actual - est;
  return { text: `${d >= 0 ? "+" : "−"}${money(Math.abs(d))}`, cls: Math.abs(d) < 1 ? "text-stone-400" : d > 0 ? "text-[#1e8449]" : "text-[#C0392B]" };
};

export function WeeklyFinanceCard() {
  const [data, setData] = useState(null);
  const [billFile, setBillFile] = useState(null);
  const [payFile, setPayFile] = useState(null);
  const [busy, setBusy] = useState("");
  const billRef = useRef(null);
  const payRef = useRef(null);

  const load = () => api.get("/finance/weekly?weeks=8").then((r) => setData(r.data))
    .catch((e) => { toast.error(formatApiError(e)); setData({ periods: [] }); });
  useEffect(() => { load(); }, []);

  const upload = async (kind) => {
    const file = kind === "billing" ? billFile : payFile;
    if (!file) { toast.error(`Choose the ${kind} file first`); return; }
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: r } = await api.post(`/finance/weekly/${kind}`, fd);
      toast.success(kind === "billing"
        ? `Billing for ${r.period_start} → ${r.period_end}: ${money(r.total)} across ${r.payers?.length} payer(s)`
        : `Payroll for ${r.period_start} → ${r.period_end}: ${money(r.total)} across ${r.employees} employee(s)`);
      setBillFile(null); setPayFile(null);
      if (billRef.current) billRef.current.value = "";
      if (payRef.current) payRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  if (data === null) return <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  const current = data.periods[0];

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="weekly-finance-card">
      <p className="font-heading text-sm font-semibold text-stone-900">Weekly billing & payroll (Saturday upload)</p>
      <p className="mt-0.5 max-w-3xl text-xs text-stone-500">
        Every Saturday, upload the payer billing file and the QuickBooks payroll detail report for the period that just ended
        (Sat–Fri). Billing and payroll won't match — payroll isn't final at billing time — so the app tracks both honestly
        against the visit-log estimate. Tuesday's meeting reviews the period once it's paid.
      </p>

      {current && (
        <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-4" data-testid="wf-current-period">
          <p className="text-xs font-semibold text-stone-800">
            Current period to upload: <span className="font-heading text-sm">{current.period_start} → {current.period_end}</span>
            <span className="ml-2 font-normal text-stone-500">billed {current.billed_date} · pays out {current.paid_date}</span>
          </p>
          <p className="mt-1 text-xs text-stone-500">
            Visit-log estimate: {current.expected?.hours ?? 0} h · billing {money(current.expected?.billing_est)} · payroll {money(current.expected?.payroll_est)}
            {" · "}{current.coverage?.visit_log_imported ? "visit log received ✓" : "visit log not imported yet"}
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Billing file (per-payer amounts)</label>
              <input ref={billRef} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setBillFile(e.target.files?.[0] || null)}
                className="block w-56 text-xs text-stone-600 file:mr-2 file:rounded-full file:border-0 file:bg-stone-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold hover:file:bg-stone-200" data-testid="wf-billing-file" />
            </div>
            <Button size="sm" onClick={() => upload("billing")} disabled={busy === "billing"}
              className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="wf-billing-upload">
              <UploadSimple size={14} className="mr-1" /> {busy === "billing" ? "Uploading…" : current.billing ? "Replace billing" : "Upload billing"}
            </Button>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">QuickBooks payroll detail</label>
              <input ref={payRef} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setPayFile(e.target.files?.[0] || null)}
                className="block w-56 text-xs text-stone-600 file:mr-2 file:rounded-full file:border-0 file:bg-stone-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold hover:file:bg-stone-200" data-testid="wf-payroll-file" />
            </div>
            <Button size="sm" onClick={() => upload("payroll")} disabled={busy === "payroll"}
              className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="wf-payroll-upload">
              <UploadSimple size={14} className="mr-1" /> {busy === "payroll" ? "Uploading…" : current.payroll ? "Replace payroll" : "Upload payroll"}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-xs" data-testid="wf-periods-table">
          <thead>
            <tr className="border-b border-stone-200 text-[10px] uppercase tracking-wide text-stone-400">
              <th className="py-1.5 pr-3">Period (Sat–Fri)</th>
              <th className="py-1.5 pr-3 text-right">Est. billing</th>
              <th className="py-1.5 pr-3 text-right">Actual billing</th>
              <th className="py-1.5 pr-3 text-right">Est. payroll</th>
              <th className="py-1.5 pr-3 text-right">Actual payroll</th>
              <th className="py-1.5 pr-3 text-right">Margin</th>
              <th className="py-1.5">On file</th>
            </tr>
          </thead>
          <tbody>
            {data.periods.map((p, i) => {
              const bv = variance(p.billing?.total, p.expected?.billing_est);
              const pv = variance(p.payroll?.total, p.expected?.payroll_est);
              const marginVal = p.margin_actual ?? (p.expected?.billing_est > 0 ? p.margin_est : null);
              const flags = [
                p.coverage?.visit_log_imported ? "visit log" : null,
                p.billing ? "billing" : null,
                p.payroll ? "payroll" : null,
                p.coverage?.missed_punches ? `${p.coverage.missed_punches} missed punch(es)` : null,
                p.coverage?.callouts ? `${p.coverage.callouts} call-out(s)` : null,
                (p.coverage?.incidents || p.coverage?.complaints) ? `${p.coverage.incidents} inc / ${p.coverage.complaints} cmp` : null,
              ].filter(Boolean);
              return (
                <tr key={p.period_start} className="border-b border-stone-100" data-testid={`wf-row-${i}`}>
                  <td className="py-1.5 pr-3 font-medium text-stone-800">{p.period_start} → {p.period_end}
                    <span className="block text-[10px] font-normal text-stone-400">paid {p.paid_date}</span></td>
                  <td className="py-1.5 pr-3 text-right text-stone-500">{money(p.expected?.billing_est)}</td>
                  <td className="py-1.5 pr-3 text-right font-medium text-stone-800">{money(p.billing?.total)}
                    {bv && <span className={`block text-[10px] ${bv.cls}`}>{bv.text} vs est</span>}</td>
                  <td className="py-1.5 pr-3 text-right text-stone-500">{money(p.expected?.payroll_est)}</td>
                  <td className="py-1.5 pr-3 text-right font-medium text-stone-800">{money(p.payroll?.total)}
                    {pv && <span className={`block text-[10px] ${pv.cls}`}>{pv.text} vs est</span>}</td>
                  <td className={`py-1.5 pr-3 text-right font-semibold ${(marginVal ?? 0) >= 0 ? "text-[#1e8449]" : "text-[#C0392B]"}`}>
                    {money(marginVal)}{p.margin_actual == null && marginVal != null && <span className="block text-[10px] font-normal text-stone-400">estimate</span>}</td>
                  <td className="py-1.5 text-stone-500">{flags.length ? flags.join(" · ") : <span className="text-stone-300">nothing yet</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
